import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { PnlNetProfitBySourceChart } from '../components/dashboard/PnlNetProfitChart';
import {
  buildNetProfitBySourceChartData, NET_PROFIT_SOURCE_SEGMENTS, NET_PROFIT_COUNTRY_SEGMENTS,
} from '../lib/pnlNetProfitChart';
import { reportsAPI, exchangeRatesAPI } from '../lib/api';
import { usePortfolioPrefsStore } from '../store';
import { convertAmount } from '../lib/currencyConvert';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { formatCompact } from '../lib/chartFormatters';
import { cn } from '../lib/utils';
import { RevenuePageContent } from './RevenuePage';
import { ExpensesPageContent } from './ExpensesPage';

/**
 * Group P&L: every business entity PLUS every personal entity, in one series.
 *
 * Data source is the canonical GET /reports/entity-pnl?scope=group
 * (services/entity_pnl.get_group_pnl), which merges the same two pipelines
 * every other scope already uses -- BR OS plus the property portfolio -- so
 * this page can never disagree with the Entities and Personal P&Ls it sits
 * above. It is an AGGREGATE of those series, not a consolidation:
 * intra-group flows are not eliminated, the same treatment "All Entities"
 * and "All Personal" use today.
 *
 * The business and personal component series are fetched alongside it purely
 * to show the split; the group totals always come from the group call itself,
 * never from adding the two components here.
 */

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] ?? currency + ' ';
  const sign = value < 0 ? '-' : '';
  return `${sign}${sym}${Math.abs(value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const num = (v) => (v == null ? 0 : parseFloat(v) || 0);

// Stat card headline: abbreviated, 2 decimals for millions (Fan,
// 2026-10-27: "just do abbreviated numbers"). Full figure in the tooltip.
const formatHeadline = (value, currency = 'GBP') => {
  const abs = Math.abs(value || 0);
  if (abs >= 1_000_000) {
    const sym = CURRENCY_SYMBOLS[currency] ?? currency + ' ';
    return `${value < 0 ? '-' : ''}${sym}${(abs / 1_000_000).toFixed(2)}M`;
  }
  return formatCompact(value || 0, currency);
};

const UK_LABEL = '\uD83C\uDDEC\uD83C\uDDE7 UK';
const FR_LABEL = '\uD83C\uDDEB\uD83C\uDDF7 France';

/** One "label ..... value" line under a stat card headline. */
function SplitLine({ label, value, currency, muted }) {
  return (
    <div className={'flex items-baseline justify-between gap-3 text-sm ' + (muted ? 'text-muted-foreground' : '')}
      title={formatAmount(value, currency)}>
      <span className={muted ? '' : 'text-muted-foreground'}>{label}</span>
      <span className={'font-mono ' + (muted ? '' : 'font-semibold text-foreground')}>{formatCompact(value, currency)}</span>
    </div>
  );
}

function GroupPnlContent() {
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [year, setYear] = useState(new Date().getFullYear());
  // Split view (Fan, 2026-10-27): by source (BR OS / Property Portfolio) or
  // by country (UK = BR OS + UK properties, France = French properties).
  const [splitView, setSplitView] = useState('source');
  const [report, setReport] = useState(null);
  const [business, setBusiness] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [fxRates, setFxRates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // /reports/entity-pnl carries no fx_rates_used, so live rates are fetched
  // separately and inverted (GBP->cur becomes cur->GBP), the same pattern
  // PersonalProfitLossPage uses.
  useEffect(() => {
    let cancelled = false;
    exchangeRatesAPI.getLatest().then((res) => {
      if (cancelled) return;
      const raw = res.data?.rates;
      if (!raw) return;
      setFxRates(Object.fromEntries(
        Object.entries(raw).map(([cur, rate]) => [cur, rate ? 1 / rate : rate])
      ));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true); else setIsLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        reportsAPI.getEntityPnl({ year, scope: 'group' }),
        reportsAPI.getEntityPnl({ year, scope: 'entities' }),
        reportsAPI.getEntityPnl({ year, scope: 'personal' }),
      ]);
      if (results[0].status === 'fulfilled') {
        setReport(results[0].value.data);
      } else {
        setReport(null);
        const msg = getErrorMessage(results[0].reason);
        toast.error(msg);
        setError(msg);
      }
      setBusiness(results[1].status === 'fulfilled' ? results[1].value.data : null);
      setPersonal(results[2].status === 'fulfilled' ? results[2].value.data : null);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [year]);

  const cv = (value, report_) => convertAmount(
    value, report_?.currency ?? 'GBP', displayCurrency, fxRates
  );

  const splitSegments = splitView === 'country' ? NET_PROFIT_COUNTRY_SEGMENTS : NET_PROFIT_SOURCE_SEGMENTS;

  const netProfitChartData = useMemo(() => {
    const fields = ['net_result', 'net_result_actual', 'net_result_forward'];
    const months = (report?.months || []).map((m) => {
      const row = { month: m.month };
      splitSegments.map((seg) => seg.key).forEach((src) => {
        fields.forEach((f) => {
          const k = `${src}_${f}`;
          row[k] = m[k] != null ? cv(num(m[k]), report) : null;
        });
      });
      return row;
    });
    return buildNetProfitBySourceChartData({ year, months }, splitSegments);
  }, [report, year, displayCurrency, fxRates, splitView]);

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (error && !report) {
    return (
      <>
        <div className="p-4 md:p-6">
          <Card>
            <CardContent className="py-16 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <h3 className="text-lg font-medium mb-2">Group P&amp;L failed to load</h3>
              <p className="text-muted-foreground">{error}</p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const summary = report?.summary || {};
  const revenue = cv(num(summary.total_revenue), report);
  const costs = cv(num(summary.total_costs), report);
  const net = cv(num(summary.net_result), report);
  const bySource = summary.by_source || {};
  const byCountry = summary.by_country || {};
  const hasCountry = !!summary.by_country;
  // Split rows for the stat cards: one line per source / country.
  const splitRows = (field) => {
    if (splitView === 'country') {
      return hasCountry ? [
        { label: UK_LABEL, value: cv(num(byCountry.uk?.[field]), report) },
        { label: FR_LABEL, value: cv(num(byCountry.fr?.[field]), report) },
      ] : [];
    }
    return summary.by_source ? [
      { label: 'Property Portfolio', value: cv(num(bySource.portfolio?.[field]), report) },
      { label: 'BR OS', value: cv(num(bySource.bros?.[field]), report) },
    ] : [];
  };
  // Same window as the dashboard Net Margin card (brosNetActualYtd in
  // GroupOverviewTab): months of this year up to the current one. Costs
  // already booked for later months (Nov/Dec) are not "to date" (Fan,
  // 2026-10-27: P&L and dashboard net profit differed).
  const nowKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const toDate = (report?.months || []).filter(
    (m) => String(m.month || '').startsWith(String(year)) && String(m.month || '') <= nowKey
  );
  const sumTo = (field) => cv(toDate.reduce((sum, m) => sum + num(m[field]), 0), report);
  const ytdActual = sumTo('net_result_actual');
  const ytdBros = sumTo('bros_net_result_actual');
  const ytdPortfolio = sumTo('portfolio_net_result_actual');
  const ytdUk = sumTo('uk_net_result_actual');
  const ytdFr = sumTo('fr_net_result_actual');
  const ytdRows = splitView === 'country'
    ? (hasCountry ? [{ label: UK_LABEL, value: ytdUk }, { label: FR_LABEL, value: ytdFr }] : [])
    : (summary.by_source ? [{ label: 'Property Portfolio', value: ytdPortfolio }, { label: 'BR OS', value: ytdBros }] : []);

  const components = [
    { label: 'Business (all entities)', data: business },
    { label: 'Personal', data: personal },
  ];

  return (
    <>
      <div className="space-y-6" data-testid="group-profit-loss-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Profit &amp; Loss</h1>
            <p className="text-sm text-muted-foreground">
              Group (all entities + personal, aggregate — intra-group flows not eliminated)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Split by">
              {[['source', 'By source'], ['country', 'By country']].map(([key, label]) => (
                <Button
                  key={key}
                  variant={splitView === key ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setSplitView(key)}
                  title={key === 'country' ? 'UK = BR OS + UK properties. France = French properties.' : 'BR OS and Property Portfolio'}
                  data-testid={`pnl-split-${key}`}
                >
                  {label}
                </Button>
              ))}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setYear(year - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium w-12 text-center">{year}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setYear(year + 1)}
              disabled={year >= new Date().getFullYear()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => fetchData(true)} disabled={isRefreshing}>
              <RefreshCw className={'h-4 w-4 mr-1' + (isRefreshing ? ' animate-spin' : '')} /> Refresh
            </Button>
            <CurrencyToggle />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Revenue (full year)</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <div className="text-2xl font-bold mb-2" title={formatAmount(revenue, displayCurrency)}>{formatHeadline(revenue, displayCurrency)}</div>
              {splitRows('total_revenue').map((r) => <SplitLine key={r.label} {...r} currency={displayCurrency} />)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Costs (full year)</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <div className="text-2xl font-bold mb-2" title={formatAmount(costs, displayCurrency)}>{formatHeadline(costs, displayCurrency)}</div>
              {splitRows('total_costs').map((r) => <SplitLine key={r.label} {...r} currency={displayCurrency} />)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Net Result (full year incl. forecast)</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <div className={'text-2xl font-bold mb-2 ' + (net < 0 ? 'text-destructive' : '')} title={formatAmount(net, displayCurrency)}>
                {formatHeadline(net, displayCurrency)}
              </div>
              {splitRows('net_result').map((r) => <SplitLine key={r.label} {...r} currency={displayCurrency} />)}
              <div className="border-t mt-2 pt-2 space-y-1">
                <SplitLine label="Actual to date" value={ytdActual} currency={displayCurrency} />
                {ytdRows.map((r) => <SplitLine key={r.label} {...r} currency={displayCurrency} muted />)}
              </div>
            </CardContent>
          </Card>
        </div>

        <PnlNetProfitBySourceChart chartData={netProfitChartData} segments={splitSegments} displayCurrency={displayCurrency} />

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Business and personal</CardTitle>
            <CardDescription>The group total split into business entities and personally held properties.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Costs</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {components.map((c) => (
                  <TableRow key={c.label}>
                    <TableCell>{c.label}</TableCell>
                    <TableCell className="text-right">
                      {c.data ? formatAmount(cv(num(c.data.summary?.total_revenue), c.data), displayCurrency) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.data ? formatAmount(cv(num(c.data.summary?.total_costs), c.data), displayCurrency) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.data ? formatAmount(cv(num(c.data.summary?.net_result), c.data), displayCurrency) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold">
                  <TableCell>Group</TableCell>
                  <TableCell className="text-right">{formatAmount(revenue, displayCurrency)}</TableCell>
                  <TableCell className="text-right">{formatAmount(costs, displayCurrency)}</TableCell>
                  <TableCell className="text-right">{formatAmount(net, displayCurrency)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Monthly</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Costs</TableHead>
                  <TableHead className="text-right">Net result</TableHead>
                  <TableHead className="text-right">of which forecast</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.months || []).map((m) => {
                  // Revenue/costs/net include forward bookings and the
                  // uninvoiced share of running BR OS stays; the last column
                  // says how much of the net is still to come.
                  const fwd = num(m.net_result_forward);
                  const isFuture = m.month > new Date().toISOString().slice(0, 7);
                  return (
                    <TableRow key={m.month} className={isFuture ? 'text-muted-foreground italic' : ''}>
                      <TableCell>{m.label || m.month}{isFuture ? ' (forecast)' : ''}</TableCell>
                      <TableCell className="text-right">
                        {m.total_revenue != null ? formatAmount(cv(num(m.total_revenue), report), displayCurrency) : '—'}
                      </TableCell>
                      <TableCell className="text-right">{formatAmount(cv(num(m.costs), report), displayCurrency)}</TableCell>
                      <TableCell className={'text-right ' + (num(m.net_result) < 0 ? 'text-destructive' : '')}>
                        {formatAmount(cv(num(m.net_result), report), displayCurrency)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fwd ? formatAmount(cv(fwd, report), displayCurrency) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// Section tabs, same as the Entities P&L page (Fan, 2026-10-27: Revenues and
// Costs tabs on the Overall P&L too). Revenues and Costs show all business
// entities; payroll is part of Costs.
const SECTION_TABS = [
  { key: 'pnl', label: 'Profit & Loss' },
  { key: 'revenue', label: 'Revenues' },
  { key: 'costs', label: 'Costs' },
];

export function GroupProfitLossPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') ?? 'pnl';
  const handleSectionChange = (key) => {
    if (key === 'pnl') setSearchParams({}); else setSearchParams({ section: key });
  };
  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6 p-4 md:p-6">
        <div
          className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit"
          data-testid="group-pnl-section-tabs"
        >
          {SECTION_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleSectionChange(tab.key)}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all',
                section === tab.key && 'bg-background text-foreground shadow'
              )}
              data-testid={'group-pnl-section-tab-' + tab.key}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {section === 'pnl' && <GroupPnlContent />}
        {section === 'revenue' && <RevenuePageContent forceAllEntities />}
        {section === 'costs' && <ExpensesPageContent forceAllEntities />}
      </div>
    </DashboardLayout>
  );
}
